import type {
  ApiResponse,
  OrganizationUserDirectoryInput,
  OrganizationUserDirectoryItem,
  OrganizationUserSortField,
  User,
} from '@/types';
import { useDemoStore } from '@/stores/demoStore';
import { labsDemoHandlers } from './labsDemo';

type Handler = (body: any) => ApiResponse<any>;

const state = () => useDemoStore.getState().data!;
const mutate = (fn: (s: ReturnType<typeof state>) => void) =>
  useDemoStore.getState().updateData(fn);
const ok = <T,>(data: T): ApiResponse<T> => ({ success: true, data });
const uuid = () => `demo-${Math.random().toString(36).slice(2, 10)}`;
const nowIso = () => new Date().toISOString();

// Deterministic synthetic numbers so charts look stable across reloads.
const seededNum = (seed: string, min: number, max: number) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const r = Math.abs(Math.sin(h)) % 1;
  return Math.floor(min + r * (max - min));
};

const studentName = (id: string) => {
  const s = state().students.find(x => x.id === id);
  return s ? `${s.firstName} ${s.lastName}` : 'Unknown Student';
};

const getStudentPlayingTimes = (userId: string) =>
  state().games.slice(0, 3).map(g => ({
    gameId: g.id,
    gameTitle: g.title,
    totalPlayingTimeSeconds: seededNum(`${userId}-${g.id}-t`, 600, 14400),
    sessionCount: seededNum(`${userId}-${g.id}-s`, 1, 25),
    lastPlayedAt: new Date(Date.now() - seededNum(`${userId}-${g.id}-l`, 1, 30) * 86400000).toISOString(),
  }));

const getStudentsWithStats = () =>
  state().students.map((student) => {
    const assignmentsCount = state().assignments.filter(a => a.studentId === student.id).length;
    const achievementsCount = state().userAchievements.filter(a => a.userId === student.id).length;
    const playingTimes = getStudentPlayingTimes(student.id);
    const totalPlayingSeconds = playingTimes.reduce((sum, item) => sum + item.totalPlayingTimeSeconds, 0);
    const lastActive = playingTimes
      .map(item => item.lastPlayedAt)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ?? student.createdAt;

    return {
      ...student,
      assignmentsCount,
      achievementsCount,
      totalPlayingHours: Number((totalPlayingSeconds / 3600).toFixed(1)),
      lastActive,
    };
  });

const getTeachersWithUsage = () =>
  state().teachers.map((teacher, index) => {
    const unitIds = state().units
      .filter((_, unitIndex) => unitIndex === index || (index === 0 && unitIndex === 2))
      .map(unit => unit.id);
    const studentIds = new Set(unitIds.flatMap(unitId => state().unitStudentIds[unitId] || []));
    const gameIds = new Set(unitIds.flatMap(unitId => state().unitGameIds[unitId] || []));
    const assignmentsCount = seededNum(`${teacher.id}-assignments`, index === 3 ? 0 : 2, index === 3 ? 1 : 16);
    const studentPlaysCount = seededNum(`${teacher.id}-plays`, index === 3 ? 0 : 12, index === 3 ? 2 : 260);

    return {
      ...teacher,
      classesCount: unitIds.length,
      studentsCount: studentIds.size,
      assignmentsCount,
      gamesUsedCount: gameIds.size,
      studentPlaysCount,
      lastActivityAt: index === 3 ? null : new Date(Date.now() - (index + 1) * 86400000).toISOString(),
    };
  });

const getUnitStaff = (unitId: string) => {
  const users = [state().currentUser, ...state().teachers, ...state().librarians];
  const ids = state().unitStaffIds[unitId] || [];
  return users.filter((user) => ids.includes(user.id));
};

const getUnitWithStaff = (unitId: string) => {
  const unit = state().units.find((item) => item.id === unitId);
  return unit ? { ...unit, assignedStaff: getUnitStaff(unitId) } : null;
};

const toUnitGame = (game: ReturnType<typeof state>['games'][number]) => ({
  ...game,
  status: game.isPublished ? 'published' as const : 'draft' as const,
});

const getUnitActivities = (unitId: string) =>
  state().activities
    .filter((activity) => activity.unitId === unitId)
    .map((activity) => ({
      ...activity,
      game: state().games.find((game) => game.id === activity.gameId),
      achievement: state().achievements.find((achievement) => achievement.id === activity.achievementId),
      responses: state().activityResponses.filter((response) => response.activityId === activity.id),
    }));

const getOrganizationDirectory = (body: Partial<OrganizationUserDirectoryInput>) => {
  const rowsByEmail = new Map<string, OrganizationUserDirectoryItem>();
  const registeredUsers = [state().currentUser, ...state().teachers, ...state().librarians, ...state().students];

  for (const user of registeredUsers) {
    rowsByEmail.set(user.email.toLowerCase(), {
      id: `user:${user.id}`,
      userId: user.id,
      instructorInvitationId: null,
      name: `${user.firstName} ${user.lastName}`.trim(),
      email: user.email,
      role: user.role === 'teacher' ? 'instructor' : user.role,
      status: 'registered',
      source: 'registered',
      invitationStatus: null,
      invitationExpiresAt: null,
      invitationLastSentAt: null,
    });
  }

  for (const invitation of state().librarianEmails) {
    const key = invitation.email.toLowerCase();
    if (rowsByEmail.has(key)) {
      continue;
    }

    rowsByEmail.set(key, {
      id: `librarian_invitation:${invitation.id}`,
      userId: null,
      instructorInvitationId: null,
      name: invitation.name,
      email: invitation.email,
      role: 'librarian',
      status: 'pending',
      source: 'librarian_invitation',
      invitationStatus: 'sent',
      invitationExpiresAt: new Date(Date.now() + 35 * 24 * 60 * 60 * 1000).toISOString(),
      invitationLastSentAt: nowIso(),
    });
  }

  for (const invitation of state().teacherEmails) {
    const key = invitation.email.toLowerCase();
    if (rowsByEmail.has(key)) {
      continue;
    }

    rowsByEmail.set(key, {
      id: `instructor_invitation:${invitation.id}`,
      userId: null,
      instructorInvitationId: invitation.id,
      name: invitation.name,
      email: invitation.email,
      role: 'instructor',
      status: 'pending',
      source: 'instructor_invitation',
      invitationStatus: 'sent',
      invitationExpiresAt: new Date(Date.now() + 35 * 24 * 60 * 60 * 1000).toISOString(),
      invitationLastSentAt: nowIso(),
    });
  }

  for (const email of state().pendingStudentEmails) {
    const key = email.toLowerCase();
    if (rowsByEmail.has(key)) {
      continue;
    }

    rowsByEmail.set(key, {
      id: `pending_student:${key}`,
      userId: null,
      instructorInvitationId: null,
      name: '',
      email,
      role: 'student',
      status: 'pending',
      source: 'pending_student',
      invitationStatus: null,
      invitationExpiresAt: null,
      invitationLastSentAt: null,
    });
  }

  const search = String(body.search || '').trim().toLowerCase();
  const filteredRows = [...rowsByEmail.values()].filter((row) => {
    const matchesSearch = !search
      || row.name.toLowerCase().includes(search)
      || row.email.toLowerCase().includes(search);
    const matchesRole = !body.role || row.role === body.role;
    const matchesStatus = !body.status || row.status === body.status;

    return matchesSearch && matchesRole && matchesStatus;
  });

  const sortField = (body.sortField || 'email') as OrganizationUserSortField;
  const direction = body.sortDirection === 'desc' ? -1 : 1;
  filteredRows.sort((a, b) => {
    const first = (sortField === 'name' ? a.name || a.email : a[sortField]).toLowerCase();
    const second = (sortField === 'name' ? b.name || b.email : b[sortField]).toLowerCase();
    const comparison = first.localeCompare(second, undefined, { numeric: true, sensitivity: 'base' });

    if (comparison !== 0) {
      return comparison * direction;
    }

    return a.email.localeCompare(b.email);
  });

  const page = Math.max(1, Number(body.page) || 1);
  const pageSize = Math.max(1, Number(body.pageSize) || 10);
  const start = (page - 1) * pageSize;

  return {
    items: filteredRows.slice(start, start + pageSize),
    total: filteredRows.length,
    page,
    pageSize,
    totalPages: Math.ceil(filteredRows.length / pageSize),
  };
};

let demoEmailPreferences = {
  newGames: true,
  newFeatures: true,
  marketing: false,
};

const handlers: Record<string, Handler> = {
  ...labsDemoHandlers,
  // ---------- auth / org ----------
  '/auth/me': () => ok(state().currentUser),
  '/auth/validate': () => ok({ valid: true, user: state().currentUser }),
  '/auth/logout': () => ok(null),
  '/auth/email-preferences/get': () => ok(demoEmailPreferences),
  '/auth/email-preferences/update': (body) => {
    demoEmailPreferences = { ...demoEmailPreferences, ...body };
    return ok(demoEmailPreferences);
  },
  '/organizations/get': () => ok(state().org),
  '/organizations/update': (body) => {
    mutate((s) => {
      if (body.region !== undefined) s.org.region = body.region;
    });
    return ok(state().org);
  },
  '/organizations/stats': () => ok(state().orgStats),
  '/organizations/users/list': (body) => ok(getOrganizationDirectory(body)),
  '/organizations/check-email-domain': () => ok({ found: false, organization: null, role: null }),
  '/organizations/game-library/preferences/get': () =>
    ok({ autoEnableNewGames: state().autoEnableNewGames }),
  '/organizations/game-library/preferences/update': ({ autoEnableNewGames }) => {
    mutate(s => {
      s.autoEnableNewGames = Boolean(autoEnableNewGames);
    });
    return ok({ autoEnableNewGames: state().autoEnableNewGames });
  },

  // ---------- games ----------
  '/games/list': () => ok(state().games),
  '/games/get': ({ gameId }) => ok(state().games.find(g => g.id === gameId) ?? null),
  '/games/create': (body) => {
    const game = { id: uuid(), appId: 9000 + state().games.length, title: '', description: '', publisher: 'Demo', version: '1.0.0', executablePath: '', isPublished: false, createdAt: nowIso(), updatedAt: nowIso(), ...body };
    mutate(s => { s.games.push(game); });
    return ok(game);
  },
  '/games/update': ({ gameId, ...patch }) => {
    let updated: any = null;
    mutate(s => {
      const i = s.games.findIndex(g => g.id === gameId);
      if (i >= 0) { s.games[i] = { ...s.games[i], ...patch, updatedAt: nowIso() }; updated = s.games[i]; }
    });
    return ok(updated);
  },
  '/games/delete': ({ gameId }) => { mutate(s => { s.games = s.games.filter(g => g.id !== gameId); }); return ok(null); },

  // ---------- users / teachers ----------
  '/users/list-students': () => ok(getStudentsWithStats()),
  '/users/list-teachers': () => ok(getTeachersWithUsage()),
  '/users/list-teacher-emails': () => ok(state().teacherEmails),
  '/users/add-teacher-email': ({ email, name }) => {
    const row = { id: uuid(), email, name, role: 'teacher' as const, isUsed: false, userId: null };
    mutate(s => { s.teacherEmails.push(row); });
    return ok({ ...row, jobId: uuid(), status: 'queued', created: true });
  },
  '/users/update-teacher-email': ({ teacherEmailId, email, name }) => {
    let updated: any = null;
    mutate(s => {
      const i = s.teacherEmails.findIndex(t => t.id === teacherEmailId);
      if (i >= 0) { if (email !== undefined) s.teacherEmails[i].email = email; if (name !== undefined) s.teacherEmails[i].name = name; updated = s.teacherEmails[i]; }
    });
    return ok(updated ? { ...updated, jobId: uuid(), status: 'queued', created: true } : updated);
  },
  '/users/resend-instructor-invitation': () => ok({ jobId: uuid(), status: 'queued', created: true }),
  '/users/delete-teacher-email': ({ teacherEmailId }) => { mutate(s => { s.teacherEmails = s.teacherEmails.filter(t => t.id !== teacherEmailId); }); return ok(null); },
  '/users/bulk-add-teacher-emails': ({ teachers }) => {
    const added: any[] = [], skipped: any[] = [], errors: any[] = [];
    mutate(s => {
      for (const t of teachers || []) {
        if (s.teacherEmails.some(e => e.email.toLowerCase() === String(t.email).toLowerCase())) { skipped.push(t); continue; }
        const row = { id: uuid(), email: t.email, name: t.name, role: 'teacher' as const, isUsed: false, userId: null };
        s.teacherEmails.push(row); added.push(row);
      }
    });
    return ok({
      added: added.map((row) => ({ ...row, jobId: uuid(), status: 'queued', created: true })),
      skipped,
      errors,
      deliveryFailed: [],
      deliveryQueued: added.map((row) => row.email),
    });
  },
  '/users/update-staff-role': ({ userId, newRole }) => {
    let updated: any = null;
    mutate(s => {
      const i = s.teacherEmails.findIndex(t => t.userId === userId);
      if (i >= 0) { s.teacherEmails[i].role = newRole; updated = s.teacherEmails[i]; }
      const userIndex = s.teachers.findIndex(t => t.id === userId);
      if (userIndex >= 0) s.teachers[userIndex].role = newRole;
    });
    return ok(updated);
  },

  // ---------- units ----------
  '/units/my-units': () => ok(state().units),
  '/units/org-units': () => ok(state().units.map((unit) => getUnitWithStaff(unit.id))),
  '/units/get': ({ unitId }) => ok(getUnitWithStaff(unitId)),
  '/units/create': (body) => {
    const unit = {
      id: uuid(),
      orgId: state().currentUser.orgId,
      code: body.code,
      name: body.name,
      year: body.year,
      semester: body.semester,
      isArchived: false,
      metadata: body.metadata,
      createdAt: nowIso(),
      updatedAt: nowIso(),
      organization: { id: state().org.id, name: state().org.name },
    };
    mutate((s) => {
      s.units.push(unit);
      s.unitStudentIds[unit.id] = [];
      s.unitGameIds[unit.id] = [];
      s.unitStaffIds[unit.id] = [s.currentUser.id];
      s.orgStats.totalUnits += 1;
      s.orgStats.activeUnits += 1;
    });
    return ok(getUnitWithStaff(unit.id));
  },
  '/units/update': ({ unitId, ...patch }) => {
    let updated = null;
    mutate((s) => {
      const index = s.units.findIndex((unit) => unit.id === unitId);
      if (index < 0) {
        return;
      }
      const wasArchived = s.units[index].isArchived;
      s.units[index] = { ...s.units[index], ...patch, updatedAt: nowIso() };
      const isArchived = s.units[index].isArchived;
      if (wasArchived !== isArchived) {
        s.orgStats.activeUnits += isArchived ? -1 : 1;
        s.orgStats.archivedUnits += isArchived ? 1 : -1;
      }
      updated = s.units[index];
    });
    return ok(updated ? getUnitWithStaff(unitId) : null);
  },
  '/units/delete': ({ unitId, confirmationCode }) => {
    const unit = state().units.find((item) => item.id === unitId);
    if (!unit || unit.code !== confirmationCode) {
      return { success: false, data: { success: false }, error: 'Unit code does not match' };
    }
    mutate((s) => {
      s.units = s.units.filter((item) => item.id !== unitId);
      delete s.unitStudentIds[unitId];
      delete s.unitGameIds[unitId];
      delete s.unitStaffIds[unitId];
      s.pendingUnitEnrollments = s.pendingUnitEnrollments.filter(
        (enrollment) => enrollment.unitId !== unitId
      );
      s.activities = s.activities.filter((activity) => activity.unitId !== unitId);
      s.orgStats.totalUnits -= 1;
      s.orgStats.activeUnits -= unit.isArchived ? 0 : 1;
      s.orgStats.archivedUnits -= unit.isArchived ? 1 : 0;
    });
    return ok({ success: true });
  },
  '/units/students': ({ unitId }) => {
    const ids = state().unitStudentIds[unitId] || [];
    const enrolledStudents = ids.flatMap((id, index) => {
      const student = state().students.find((candidate) => candidate.id === id);
      if (!student) {
        return [];
      }
      return [{
        id: student.id,
        email: student.email,
        firstName: student.firstName,
        lastName: student.lastName,
        role: 'student',
        createdAt: student.createdAt,
        status: 'enrolled' as const,
        totalPlayingHours: Number((1.5 + index * 0.7).toFixed(1)),
        lastActive: new Date(Date.now() - (index + 1) * 86400000).toISOString(),
      }];
    });
    const pendingStudents = state().pendingUnitEnrollments
      .filter((enrollment) => enrollment.unitId === unitId)
      .map((enrollment) => ({
        id: enrollment.id,
        email: enrollment.email,
        firstName: null,
        lastName: null,
        role: 'student',
        createdAt: enrollment.createdAt,
        status: 'pending' as const,
        totalPlayingHours: 0,
        lastActive: null,
      }));

    return ok([...enrolledStudents, ...pendingStudents]);
  },
  '/units/games': ({ unitId }) => {
    const ids = state().unitGameIds[unitId] || [];
    return ok(state().games.filter(g => ids.includes(g.id)).map(toUnitGame));
  },
  '/units/teachers': ({ unitId }) => ok(getUnitStaff(unitId)),
  '/units/add-teachers-by-email': ({ unitId, emails }) => {
    const added: User[] = [];
    const pending: string[] = [];
    const alreadyPending: string[] = [];
    const notFound: string[] = [];
    const alreadyAssigned: string[] = [];
    const notTeacher: string[] = [];
    const users = [state().currentUser, ...state().teachers, ...state().librarians];
    mutate((s) => {
      for (const email of emails || []) {
        const normalized = String(email).toLowerCase();
        const user = users.find((candidate) => candidate.email.toLowerCase() === normalized);
        if (!user) {
          notFound.push(normalized);
        } else if (!['teacher', 'librarian'].includes(user.role)) {
          notTeacher.push(normalized);
        } else if ((s.unitStaffIds[unitId] || []).includes(user.id)) {
          alreadyAssigned.push(normalized);
        } else {
          s.unitStaffIds[unitId] = [...(s.unitStaffIds[unitId] || []), user.id];
          added.push(user);
        }
      }
    });
    return ok({ added, pending, alreadyPending, notFound, alreadyAssigned, notTeacher });
  },
  '/units/remove-teacher': ({ unitId, teacherId }) => {
    mutate((s) => {
      s.unitStaffIds[unitId] = (s.unitStaffIds[unitId] || []).filter((id) => id !== teacherId);
    });
    return ok({ success: true });
  },
  '/units/add-students-by-email': ({ unitId, emails }) => {
    const result = {
      added: [] as User[],
      pending: [] as string[],
      alreadyEnrolled: [] as string[],
      alreadyPending: [] as string[],
      invalidDomain: [] as string[],
      invalidFormat: [] as string[],
      invitationEmailFailed: [] as string[],
      invitationBatchId: uuid(),
      invitationJobsQueued: 0,
    };
    mutate((s) => {
      for (const rawEmail of emails || []) {
        const email = String(rawEmail).trim().toLowerCase();
        if (!email.includes('@')) {
          result.invalidFormat.push(email);
          continue;
        }
        if (!email.endsWith('@demo-students.ludolio.com')) {
          result.invalidDomain.push(email);
          continue;
        }
        const pendingEnrollment = s.pendingUnitEnrollments.find(
          (enrollment) =>
            enrollment.unitId === unitId &&
            enrollment.email.toLowerCase() === email
        );
        if (pendingEnrollment) {
          result.alreadyPending.push(email);
          continue;
        }

        const student = s.students.find((candidate) => candidate.email.toLowerCase() === email);
        if (student && (s.unitStudentIds[unitId] || []).includes(student.id)) {
          result.alreadyEnrolled.push(email);
          continue;
        }
        if (!student) {
          s.pendingUnitEnrollments.push({
            id: uuid(),
            unitId,
            email,
            createdAt: nowIso(),
          });
          result.pending.push(email);
          result.invitationJobsQueued += 1;
          continue;
        }

        result.added.push(student);
        s.unitStudentIds[unitId] = [...(s.unitStudentIds[unitId] || []), student.id];
      }
    });
    return ok(result);
  },
  '/units/invitation-email-batch-status': ({ batchId }) => ok({
    id: batchId,
    totalJobs: 1,
    completedAt: nowIso(),
    counts: { sent: 1 },
  }),
  '/units/resend-student-invitation': () => ok({ jobId: uuid(), status: 'queued', created: true }),
  '/units/remove-student': ({ unitId, studentId }) => {
    mutate((s) => {
      s.unitStudentIds[unitId] = (s.unitStudentIds[unitId] || []).filter((id) => id !== studentId);
    });
    return ok({ success: true });
  },
  '/units/remove-pending-enrollment': ({ unitId, pendingId }) => {
    mutate((s) => {
      s.pendingUnitEnrollments = s.pendingUnitEnrollments.filter(
        (enrollment) =>
          enrollment.id !== pendingId || enrollment.unitId !== unitId
      );
    });
    return ok({ success: true });
  },

  // ---------- activities ----------
  '/activities/list': ({ unitId }) => ok(getUnitActivities(unitId)),
  '/activities/responses': ({ activityId }) =>
    ok(state().activityResponses.filter((response) => response.activityId === activityId)),

  // ---------- assignments ----------
  '/assignments/create': (body) => {
    const g = state().games.find(x => x.id === body.gameId);
    const st = state().students.find(x => x.id === body.studentId);
    const assignment = { id: uuid(), assignedBy: state().currentUser.id, createdAt: nowIso(), updatedAt: nowIso(), game: g, student: st, ...body };
    mutate(s => { s.assignments.push(assignment); });
    return ok(assignment);
  },
  '/assignments/student-games': ({ studentId }) => ok(state().assignments.filter(a => a.studentId === studentId)),
  '/assignments/update': ({ assignmentId, ...patch }) => {
    let updated: any = null;
    mutate(s => {
      const i = s.assignments.findIndex(a => a.id === assignmentId);
      if (i >= 0) { s.assignments[i] = { ...s.assignments[i], ...patch, updatedAt: nowIso() }; updated = s.assignments[i]; }
    });
    return ok(updated);
  },
  '/assignments/delete': ({ assignmentId }) => { mutate(s => { s.assignments = s.assignments.filter(a => a.id !== assignmentId); }); return ok(null); },
  '/assignments/validate-access': ({ studentId, gameId }) => {
    const a = state().assignments.find(x => x.studentId === studentId && x.gameId === gameId && x.isActive);
    return ok({ hasAccess: !!a, assignment: a });
  },

  // ---------- achievements ----------
  '/achievements/create': (body) => {
    const row = { id: uuid(), points: 10, isSecret: false, createdAt: nowIso(), ...body };
    mutate(s => { s.achievements.push(row); });
    return ok(row);
  },
  '/achievements/game-achievements': ({ gameId }) => ok(state().achievements.filter(a => a.gameId === gameId)),
  '/achievements/user-achievements': ({ userId, gameId }) => ok(state().userAchievements.filter(a => a.userId === userId && a.gameId === gameId)),
  '/achievements/all-user-achievements': ({ userId }) => ok(state().userAchievements.filter(a => a.userId === userId)),

  // ---------- stats (game-level stat definitions) ----------
  '/stats/game-stats': ({ gameId }) => ok(state().gameStats.filter(s => s.gameId === gameId)),
  '/stats/create': (body) => {
    const row = { id: uuid(), incrementOnly: false, aggregated: false, defaultValue: '0', createdAt: nowIso(), ...body };
    mutate(s => { s.gameStats.push(row); });
    return ok(row);
  },
  '/stats/delete': ({ statId }) => { mutate(s => { s.gameStats = s.gameStats.filter(x => x.id !== statId); }); return ok(null); },

  // ---------- statistics (aggregated play-time/installations) ----------
  '/statistics/org-games-overview': () => {
    const { currentUser, teachers, students } = state();
    const orgUserCount = new Set(
      [currentUser, ...teachers, ...students]
        .filter((user) => user.orgId === currentUser.orgId)
        .map((user) => user.id),
    ).size;
    return ok(state().games.map((game) => ({
      gameId: game.id,
      uniquePlayersCount: seededNum(`${game.id}-players`, 0, orgUserCount + 1),
    })));
  },
  '/statistics/unit-statistics': ({ unitId }) => {
    const gameIds = state().unitGameIds[unitId] || [];
    const studentIds = state().unitStudentIds[unitId] || [];
    return ok(gameIds.map(gid => {
      const g = state().games.find(x => x.id === gid);
      return {
        gameId: gid, gameTitle: g?.title || gid,
        totalPlayingTimeSeconds: seededNum(`${unitId}-${gid}-time`, 900, 36000),
        totalSessions: seededNum(`${unitId}-${gid}-sess`, 5, 80),
        uniquePlayersCount: Math.min(studentIds.length, seededNum(`${unitId}-${gid}-uniq`, 1, studentIds.length + 1)),
        installationCount: seededNum(`${unitId}-${gid}-inst`, 2, 25),
      };
    }));
  },
  '/statistics/unit-game-playing-time': ({ unitId, gameId }) => {
    const ids = state().unitStudentIds[unitId] || [];
    return ok(ids.map((id, i) => ({
      userId: id, userName: studentName(id),
      totalPlayingTimeSeconds: seededNum(`${unitId}-${gameId}-${id}-t`, 300, 7200),
      sessionCount: seededNum(`${unitId}-${gameId}-${id}-s`, 1, 20),
      lastPlayedAt: new Date(Date.now() - (i + 1) * 86400000).toISOString(),
    })));
  },
  '/statistics/unit-game-installations': ({ unitId, gameId }) => {
    const ids = state().unitStudentIds[unitId] || [];
    return ok(ids.map((id, i) => ({
      userId: id, userName: studentName(id),
      installationCount: seededNum(`${unitId}-${gameId}-${id}-i`, 1, 4),
      lastInstalledAt: new Date(Date.now() - (i + 2) * 86400000).toISOString(),
    })));
  },
  '/statistics/user-playing-times': ({ userId }) => {
    return ok(getStudentPlayingTimes(userId));
  },
};

export function handleDemoRequest(url: string, body: any): ApiResponse<any> {
  // Strip everything up to and including the API version prefix so '/api/v1/games/list' → '/games/list'.
  const path = url.replace(/^.*\/api\/v\d+/, '') || url;
  const handler = handlers[path];
  if (!handler) {
    console.warn('[demo] no handler for', path, '— returning empty success');
    return ok(null);
  }
  return handler(body ?? {});
}
